import type { MailboxInfo } from 'owa-client-types';
import type { SoftBlockOnSendDialogTypeEnum, ExtensionEventResult } from 'owa-addins-store';
import type { TaskPaneCollectionProps } from './components/TaskPaneCollection';
import type { AddinsFlexPaneBodyProps } from './components/AddinsFlexPaneBody';
import type { AddinsPaneHeaderCollectionProps } from './components/AddinsFlexPaneHeader';
import type { CopilotAppViewProps } from './components/CopilotAppView';
import type * as lazyIndex from './lazyIndex';
import { LazyAction, type LazyComponent, LazyModule, createLazyComponent } from 'owa-bundling';

const lazyModule = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "AddinsView" */ './lazyIndex'),
    { name: 'AddinsView' }
);

export const CopilotAppView: LazyComponent<CopilotAppViewProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.CopilotAppView);

export const lazyCreateAddinCommandSurfaceAction: LazyAction<
    typeof lazyIndex.createAddinCommandSurfaceAction,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.createAddinCommandSurfaceAction);
export const lazyOnSendAddinItemSwitch: LazyAction<
    typeof lazyIndex.onSendAddinItemSwitch,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.onSendAddinItemSwitch);
export const lazyConfirmOfflineAddinSend: LazyAction<
    typeof lazyIndex.confirmOfflineAddinSend,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.confirmOfflineAddinSend);
export const lazyOnLaunchEventTriggered: LazyAction<
    typeof lazyIndex.onLaunchEventTriggered,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.onLaunchEventTriggered);
export const lazyOnHostItemChanged: LazyAction<
    typeof lazyIndex.onHostItemChanged,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.onHostItemChanged);

export const lazyChildWindowMessageListener: LazyAction<
    typeof lazyIndex.childWindowMessageListener,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.childWindowMessageListener);
export const AddinsFlexPaneHeader: LazyComponent<AddinsPaneHeaderCollectionProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.AddinsFlexPaneHeader);
export const AddinsFlexPaneBody: LazyComponent<AddinsFlexPaneBodyProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.AddinsFlexPaneBody);
export const TaskPaneCollection: LazyComponent<TaskPaneCollectionProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.TaskPaneCollection);
export const lazyOnItemNavigation: LazyAction<
    typeof lazyIndex.onItemNavigation,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.onItemNavigation);
export const lazyAllowItemSendAndBlockOnSendAddinEvents: LazyAction<
    typeof lazyIndex.allowItemSendAndBlockOnSendAddinEvents,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.allowItemSendAndBlockOnSendAddinEvents);
export const lazyOnComposeClose: LazyAction<
    typeof lazyIndex.onComposeClose,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.onComposeClose);
export const lazyShowOfflineModeDialog: LazyAction<
    typeof lazyIndex.showOfflineModeDialog,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.showOfflineModeDialog);
export const lazyCloseTaskPaneAddinCommand: LazyAction<
    typeof lazyIndex.closeTaskPaneAddinCommand,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.closeTaskPaneAddinCommand);
export const lazyOpenContextualCallout: LazyAction<
    typeof lazyIndex.openContextualCallout,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.openContextualCallout);
export const lazyLaunchAddinCommand: LazyAction<
    typeof lazyIndex.launchAddinCommand,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.launchAddinCommand);
export const lazyAddinCommandSurfaceItemsObserver: LazyAction<
    typeof lazyIndex.observeAddinCommandSurfaceItems,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.observeAddinCommandSurfaceItems);
export const lazyInitializeExecuteEntryPoint: LazyAction<
    typeof lazyIndex.ExecuteEntryPoint,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.ExecuteEntryPoint);
export const lazyRemoveUninstalledIntegratedSpamAddinEntriesFromLocalStorage: LazyAction<
    typeof lazyIndex.removeUninstalledIntegratedSpamAddinEntriesFromLocalStorage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.removeUninstalledIntegratedSpamAddinEntriesFromLocalStorage);
export const lazySetSoftBlockDialogState: LazyAction<
    (
        tabId: string,
        newDialogState: SoftBlockOnSendDialogTypeEnum,
        mailboxInfo: MailboxInfo,
        errorMessage?: string,
        showSendAnywayOption?: boolean,
        shouldOpenTaskPane?: boolean,
        addinExecutionResult?: ExtensionEventResult
    ) => void,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setSoftBlockDialogState);
export const lazyInitializeExtensibilityFrameworkComponent: LazyAction<
    typeof lazyIndex.initializeExtensibilityFrameworkComponent,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.initializeExtensibilityFrameworkComponent);
export const lazyRemoveExtensibilityFrameworkComponent: LazyAction<
    typeof lazyIndex.removeExtensibilityFrameworkComponent,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.removeExtensibilityFrameworkComponent);
export const lazyOpenNonPersistentTaskPaneAddinCommand: LazyAction<
    typeof lazyIndex.openNonPersistentTaskPaneAddinCommand,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.openNonPersistentTaskPaneAddinCommand);
export const lazyLaunchAutoRunAddinsById: LazyAction<
    typeof lazyIndex.launchAutoRunAddinsById,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.launchAutoRunAddinsById);
